import { ProductFields } from "@tenforce/product-fields"
import { ColorName } from "@tenforce/toolbox-default-style"

import env from "../../env"
import * as FieldCode from "./constants"
import { getFormatBasedOnType, parseOdataDateValue } from "./dateTimeParsing"
import { isFieldTypeSingleSelect } from "../../components/fields/components/treeSelector/utils"
import { parseTagData } from "../tagPicker"
import { parseItemCollectionData } from "../treeSelector/utils"
import { guid } from "../../util/Guid"
import { convertToProductFieldAttachment } from "./attachmentParsing"
import { removeNewlinesAndTabs } from "./htmlParsing"
import {
    ActionComponentFieldDefinitionDTO,
    ActionFieldDTO,
    CheckboxFieldDTO,
    ConceptFieldDTO,
    DateFieldDTO,
    InlineAttachmentsFieldDTO,
    ItemActionDTO,
    ItemDTO,
    ItemViewActionsComponentDTO,
    ItemViewComponentDTO,
    ItemViewGroupingComponentDTO,
    LocationFieldDTO,
    MapComponentDTO,
    MultiFieldsComponentDTO,
    NumberComponentFieldDefinitionDTO,
    NumberFieldDTO,
    NumberFormat,
    OptionItemDTO,
    SingleFieldComponentDTO,
    TagDTO,
    TextComponentFieldDefinitionDTO,
    TextFieldDTO,
    TimeFieldDTO,
    ToggleButtonFieldDTO,
} from "../models/fields"
import {
    ICheckboxField,
    IConceptField,
    IDateField,
    IField,
    IInlineAttachmentField,
    IInlineButtonsField,
    IItemPickerField,
    IItemRelationField,
    ILocationCoordinates,
    ILocationField,
    IMapField,
    INumberField,
    IRichTextField,
    ISectionField,
    ITagPickerField,
    ITextField,
    ITimeField,
    IToggleButtonField,
    ITreeSelectorField,
} from "../../components/fields"
import { IButton, ICallButtonData } from "../../components/common/contextmenu"
import { IUserFormattingSettings } from "../../components/applicationheader/components/usermenu"
import { parseSelectorData } from "../treeSelector/treeSelector"
import { Type, TypeModel } from "../models/treeSelector/models"
import { apiPath } from "../api/data"

const defaultNumberFormatProps = {
    symbol: "",
    symbolAtStart: true,
    addSpaceToSymbol: true,
    decimalDigits: 0,
    decimalSeparator: ".",
    groupSeparator: "",
    removeTrailingZeros: true,
}

// In the future, we'll calculate those based on what the API sends, for now, it's hardcoded.
const maxIntegerCount = 10
const maxDecimalCount = 6

export const parseFieldData = (
    component: ItemViewComponentDTO,
    userFormattingSettings: IUserFormattingSettings,
): IField => {
    const {
        ComponentDefinition: {
            Description,
            Label,
            PositionX,
            PositionY,
            ["@type"]: odataType,
        } = {},
        ComponentDefinition,
        ComponentCode,
        IsShownAsReadOnly,
    } = component

    const fieldData: IField = {
        description: Description ?? "",
        fieldId: ComponentCode,
        // we won't have separate isDisabled, in the backend it's the same as isReadOnly
        // but UX wise we'll use isReadOnly design
        isDisabled: false,
        isReadOnly: IsShownAsReadOnly,
        isRequired:
            "Field" in component
                ? (component as SingleFieldComponentDTO)?.Field?.IsRequired ??
                  false
                : false,
        position: {
            x: PositionX ?? 0,
            y: PositionY ?? 0,
        },
        title: Label ?? "",
        type: "field",
        odataType: odataType ?? "",
        value: null,
        itemType: "",
    }

    // we need to know that it is some kind of text field
    if (odataType === "#TF.TextConceptViewFieldComponent") {
        const { HasMultipleLines, MaxLength, Mode } =
            ComponentDefinition as TextComponentFieldDefinitionDTO
        const Field = (component as SingleFieldComponentDTO).Field
        const { Value, HasAutoCompletion } = Field as TextFieldDTO

        if (Mode === "RTF") {
            return {
                ...fieldData,
                type: "richText",
                itemId: Field["ItemId"],
                value: removeNewlinesAndTabs(Value || ""),
                odataType: Field["@type"],
                maxLength: MaxLength,
            } as IRichTextField
        }

        let type = "smallText"
        if (HasMultipleLines) {
            type = "bigText"
        } else if (MaxLength > 80) {
            type = "longText"
        }

        return {
            ...fieldData,
            isAutoCompleteEnabled: HasAutoCompletion || false,
            itemId: Field["ItemId"],
            maxLength: MaxLength,
            type,
            value: Value?.toString() ?? "",
            // this specifies what kind of text field it is
            odataType: Field["@type"],
        } as ITextField
    } else if (odataType === "#TF.TimeConceptViewFieldComponent") {
        const Field = (component as SingleFieldComponentDTO).Field
        const { Value } = Field as TimeFieldDTO
        const parsedValue = parseOdataDateValue(
            Value ?? "",
            getFormatBasedOnType(Field["@type"]),
        )

        return {
            ...fieldData,
            type: "time",
            value: parsedValue,
            locale: env.culture.includes("US") ? "en" : "fr",
            timeZone: userFormattingSettings?.timeZone?.id ?? "default",
            odataType: Field["@type"],
        } as ITimeField
    } else if (odataType === "#TF.NumberConceptViewFieldComponent") {
        const Field = (component as SingleFieldComponentDTO).Field
        const { Value, HasAutoCompletion } = Field as NumberFieldDTO
        const apiFormattingSettings =
            ComponentDefinition as NumberComponentFieldDefinitionDTO

        const format = ensureDefaultFormatSettingsAreSet(
            apiFormattingSettings.Formatting,
            userFormattingSettings,
        )

        return {
            ...fieldData,
            isAutoCompleteEnabled: HasAutoCompletion || false,
            itemId: Field["ItemId"],
            type: "number",
            value: Value,
            odataType: Field["@type"],
            format,
            maxNumberOfIntegerDigits: maxIntegerCount,
            maxNumberOfDecimalDigits: maxDecimalCount,
        } as INumberField
    } else if (odataType === "#TF.BoolConceptViewFieldComponent") {
        const Field = (component as SingleFieldComponentDTO).Field
        const { Value } = Field as CheckboxFieldDTO

        return {
            ...fieldData,
            type: "checkbox",
            value: Value,
            odataType: Field["@type"],
        } as ICheckboxField
    } else if (odataType === "#TF.DateConceptViewFieldComponent") {
        const Field = (component as SingleFieldComponentDTO).Field
        const { Value } = Field as DateFieldDTO

        const parsedValue = parseOdataDateValue(
            Value ?? "",
            getFormatBasedOnType(Field["@type"]),
        )

        return {
            ...fieldData,
            type: "date",
            value: parsedValue,
            locale: env.culture.includes("US") ? "en" : "fr",
            timeZone: userFormattingSettings?.timeZone?.id ?? "default",
            odataType: Field["@type"],
        } as IDateField
    } else if (
        odataType === "#TF.SelectorConceptViewFieldComponent" &&
        ComponentDefinition.Type === "Toggle"
    ) {
        const Field = (component as SingleFieldComponentDTO).Field
        const { Value } = Field as ToggleButtonFieldDTO
        let value: ProductFields.ToggleItem | null = null

        if (!!Value) {
            value = parseOptionField(Value, true)
        }

        return {
            ...fieldData,
            type: "toggle",
            value: value,
            odataType: Field["@type"],
        } as IToggleButtonField
    } else if (
        odataType === "#TF.SelectorConceptViewFieldComponent" &&
        ComponentDefinition.ComponentCode === FieldCode.ConceptCode
    ) {
        const Field = (component as SingleFieldComponentDTO).Field
        const { ItemType, Label, Id } = Field.Value as ConceptFieldDTO

        return {
            ...fieldData,
            type: "concept",
            itemType: ItemType,
            label: Label,
            id: Id,
            odataType: Field["@type"],
        } as IConceptField
    }

    // we need to know that it is some kind of selector field
    else if (odataType === "#TF.SelectorConceptViewFieldComponent") {
        const Field = (component as SingleFieldComponentDTO).Field
        const { Value } = Field

        if (Field["@type"] === "#TF.TagsItemField") {
            const Value = Field.Value as TagDTO[]
            return {
                ...fieldData,
                type: "tagPicker",
                itemId: Field["ItemId"],
                value: Value.map(parseTagData),
            } as ITagPickerField
        }
        const odataType = Field["@type"].substring(1) as Type

        if (component.ComponentDefinition.Type === "Item") {
            if (Field["@type"] === "#TF.ItemRelationItemField") {
                return {
                    ...fieldData,
                    type: "itemRelation",
                    value: parseItemCollectionData(Value as ItemDTO),
                    // We force the generic type.
                    odataType: "TF.ItemsItemField",
                    itemId: Field["ItemId"],
                    // The item relation is always multi select.
                    isSingleSelect: false,
                } as IItemRelationField
            } else {
                return {
                    ...fieldData,
                    type: "itemPicker",
                    value: parseItemCollectionData(Value as ItemDTO),
                    odataType: odataType,
                    itemId: Field["ItemId"],
                    isSingleSelect: isFieldTypeSingleSelect(odataType),
                } as IItemPickerField
            }
        }

        return {
            ...fieldData,
            type: "selector",
            value: parseSelectorData(
                Value as TypeModel[`${typeof odataType}`],
                odataType,
            ),
            odataType: odataType,
            itemId: Field["ItemId"],
            isSingleSelect: isFieldTypeSingleSelect(odataType),
        } as ITreeSelectorField
    } else if (odataType === "#TF.AttachmentsConceptViewFieldComponent") {
        const Field = (component as SingleFieldComponentDTO).Field
        const { Value } = Field as InlineAttachmentsFieldDTO
        const parsedValue = Value?.map((attachment) =>
            convertToProductFieldAttachment(
                attachment,
                undefined,
                userFormattingSettings,
            ),
        )
        return {
            ...fieldData,
            type: "inlineAttachment",
            itemId: Field["ItemId"],
            value: parsedValue || [],
        } as IInlineAttachmentField
    } else if (
        odataType === "#TF.ConceptViewSectionComponent" &&
        ComponentDefinition.Type === "Map"
    ) {
        const mapComponent = component as MapComponentDTO
        const Components = mapComponent.Components as SingleFieldComponentDTO[]

        const [keyType = "", keyValue = ""] = (env.googleMapsKey ?? "").split(
            "=",
        )
        if (keyType === "client") {
            console.error(
                "Google Client IDs should no longer be used for API calls. Please create an API key instead.",
            )
        }

        const mapFieldValue: IField[] = []
        Components.forEach((locationField) => {
            const locationItemField = locationField.Field as LocationFieldDTO
            const coordinates = parseLocationCoordinates(
                locationItemField.Value,
            )
            const { MapMarker } = locationItemField
            if (MapMarker) {
                mapFieldValue.push({
                    type: "location",
                    odataType: locationItemField["@type"],
                    fieldId: locationItemField.FieldDefinitionId,
                    title: locationField.ComponentDefinition.Label,
                    isReadOnly: locationField.IsShownAsReadOnly,
                    value: {
                        ...coordinates,
                        marker: {
                            id: MapMarker.Id,
                            imageSrc: `${env.applicationPath}/${apiPath}/MapMarkers(${MapMarker.Id})/$value`,
                            anchor: [MapMarker.AnchorX, MapMarker.AnchorY],
                            size: [MapMarker.ImageWidth, MapMarker.ImageHeight],
                        },
                    },
                    itemType: "",
                })
            } else {
                console.error(
                    `No Map-Marker for location ${locationItemField.FieldDefinitionId}`,
                )
            }
        })

        return {
            ...fieldData,
            googleApiKey: keyValue,
            type: "map",
            itemId: mapComponent.ItemId,
            odataType: "#TF.MapConceptViewFieldComponent",
            isReadOnly: mapComponent.IsShownAsReadOnly,
            value: mapFieldValue,
        } as IMapField
    } else if (odataType === "#TF.LocationConceptViewFieldComponent") {
        const Field = (component as SingleFieldComponentDTO).Field
        const locationField = Field as LocationFieldDTO
        const coordinates = parseLocationCoordinates(locationField.Value)
        const { MapMarker } = locationField
        if (!MapMarker) {
            console.error(
                `No Map-Marker for location ${locationField.FieldDefinitionId}`,
            )
        }
        return {
            ...fieldData,
            type: "location",
            itemId: component.ItemId,
            odataType: locationField["@type"],
            value: {
                ...coordinates,
                marker: MapMarker
                    ? {
                          id: MapMarker.Id,
                          imageSrc: `${env.applicationPath}/${apiPath}/MapMarkers(${MapMarker.Id})/$value`,
                          anchor: [MapMarker.AnchorX, MapMarker.AnchorY],
                          size: [MapMarker.ImageWidth, MapMarker.ImageHeight],
                      }
                    : null,
            },
            isReadOnly: component.IsShownAsReadOnly ?? false,
        } as ILocationField
    } else if (
        odataType === "#TF.ConceptViewSectionComponent" &&
        ComponentDefinition.Type === "Label"
    ) {
        const section = component as ItemViewGroupingComponentDTO

        return {
            ...fieldData,
            type: "section",
            fields: section.Components.map((field) =>
                parseFieldData(
                    field as SingleFieldComponentDTO | MultiFieldsComponentDTO,
                    userFormattingSettings,
                ),
            ),
            description: ComponentDefinition.Description,
            label: ComponentDefinition.Label,
        } as ISectionField
    } else if (odataType === "#TF.ConceptViewActionsComponent") {
        const componentActions = (component as ItemViewActionsComponentDTO)
            .Actions
        const fieldDefinitionActions = (
            component.ComponentDefinition as ActionComponentFieldDefinitionDTO
        ).Actions

        const actions = componentActions
            .filter(
                ({ ActionId: componentActionId }) =>
                    fieldDefinitionActions.findIndex(
                        ({ ActionId: fieldDefinitionActionId }) =>
                            componentActionId === fieldDefinitionActionId,
                    ) > -1,
            )
            .sort((a, b) => {
                const seqA = a.SequenceNr ?? Number.MAX_SAFE_INTEGER
                const seqB = b.SequenceNr ?? Number.MAX_SAFE_INTEGER

                if (seqA !== seqB) {
                    return seqA - seqB
                }

                const fieldA = fieldDefinitionActions.find(
                    (fd) => fd.ActionId === a.ActionId,
                )
                const fieldB = fieldDefinitionActions.find(
                    (fd) => fd.ActionId === b.ActionId,
                )

                const labelA = fieldA?.Action?.Label || ""
                const labelB = fieldB?.Action?.Label || ""

                const labelCompareResult = labelA.localeCompare(labelB)

                if (labelCompareResult !== 0) {
                    return labelCompareResult
                }

                const idA = fieldA?.ActionId || ""
                const idB = fieldB?.ActionId || ""
                return idA.localeCompare(idB)
            })
            .map((componentAction) =>
                fieldDefinitionActions.find(
                    (fieldDefinitionAction) =>
                        fieldDefinitionAction.ActionId ===
                        componentAction.ActionId,
                ),
            )

        return {
            ...fieldData,
            type: "inlineButtons",
            itemId: component.ItemId,
            value: actions.map(parseInlineAction),
            odataType: odataType,
        } as IInlineButtonsField
    } else if (odataType === "#TF.ConceptViewFieldComponent") {
        // We turn this explanation field into a read only text field.
        return {
            ...fieldData,
            description: fieldData.description,
            isReadOnly: true,
            itemId: component.ItemId,
            type: "bigText",
            value: "",
            odataType: ComponentDefinition.Type,
        } as ITextField
    }

    return fieldData
}

export const parseOptionField = (
    apiResponse: OptionItemDTO,
    isSelected = false,
): ProductFields.ToggleItem => {
    return {
        value: apiResponse.Label,
        valueId: apiResponse.Id.toString(),
        isSelected: isSelected,
        color: apiResponse.Color as ColorName,
        isActive: apiResponse.IsActive,
    } as ProductFields.ToggleItem
}

export const parseItemAction = (apiResponse: ItemActionDTO): IButton => {
    return {
        uuid: guid(),
        id: apiResponse.ActionId,
        caption: apiResponse.Action.Label,
        icon: apiResponse.Action.Icon,
        tooltip: apiResponse.Action.Description,
        isLoading: false,
        isCustom: !apiResponse.Action.IsFactoryDefault,
        inOptionalMenu: apiResponse.Type != "Primary",
        showCaption: true,
        buttonData: {
            type: "Call",
            actionId: apiResponse.ActionId,
        } as ICallButtonData,
    }
}

export const parseInlineAction = (
    apiResponse: ActionFieldDTO,
): ProductFields.InlineAction => {
    return {
        id: apiResponse.ActionId,
        label: apiResponse.Action.Label,
        icon:
            apiResponse.Action.Icon === "NoIcon" ? "" : apiResponse.Action.Icon,
        description: apiResponse.Action.Description,
        isDisabled: false,
        isReadOnly: false,
        isSecondary: false,
        isTransparent: true,
        color: apiResponse.Color,
    }
}

export const parseLocationCoordinates = (
    Value: LocationFieldDTO["Value"],
): ILocationCoordinates | null => {
    if (!Value) {
        return null
    }
    const { Point } = Value
    return {
        latitude: Point.coordinates[1],
        longitude: Point.coordinates[0],
    }
}

function ensureDefaultFormatSettingsAreSet(
    apiFormattingSettings: NumberFormat | null,
    userRegionalSettings: IUserFormattingSettings,
): ProductFields.NumberFormatProps {
    if (apiFormattingSettings) {
        return {
            addSpaceToSymbol: true,
            decimalDigits:
                apiFormattingSettings.DecimalPlaces ??
                defaultNumberFormatProps.decimalDigits,
            decimalSeparator: apiFormattingSettings.DecimalSeparator
                ? apiFormattingSettings.DecimalSeparator
                : userRegionalSettings?.numberFormat?.decimalSeparator ??
                  defaultNumberFormatProps.decimalSeparator,
            groupSeparator:
                apiFormattingSettings.GroupSeparator ??
                defaultNumberFormatProps.groupSeparator,
            removeTrailingZeros: !!apiFormattingSettings.DecimalPlaces
                ? false
                : true,
            symbol:
                apiFormattingSettings.Unit ?? defaultNumberFormatProps.symbol,
            symbolAtStart: !apiFormattingSettings?.UnitFollowsValue,
        }
    } else {
        return {
            ...defaultNumberFormatProps,
            decimalSeparator:
                userRegionalSettings?.numberFormat?.decimalSeparator ??
                defaultNumberFormatProps.decimalSeparator,
            decimalDigits: maxDecimalCount,
        }
    }
}
